import { white } from '@kmns-internal/furikake'
import {
  Button,
  Checkbox,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import {
  type ExcelConversionLog,
  ExcelConversionLogStatusEnum,
  type PlaceNode,
} from '@ulysses-inc/harami_api_client'
import { type Dispatch, type FC, type SetStateAction, useEffect } from 'react'
import date from 'src/exShared/util/date'
import { useMutationDownloadExcelConversionLogFile } from './api/useMutationDownloadExcelConversionLogFile'
import { getStatusBadge } from './ExcelConversionLogStatus'

interface OwnProps {
  logs: ExcelConversionLog[]
  goFlows: () => void
  chooseLogUUIDs: { [key: string]: string }
  setChooseLogUUIDs: Dispatch<SetStateAction<{ [key: string]: string }>>
}

const ExcelConversionLogList: FC<OwnProps> = (props: OwnProps) => {
  const { mutate: downloadSingleFile } =
    useMutationDownloadExcelConversionLogFile()

  useEffect(() => {
    Object.keys(props.chooseLogUUIDs).forEach(logUUID => {
      const isContain =
        props.logs.findIndex(log => log.logFile?.file?.uuid === logUUID) >= 0
      if (!isContain) {
        // eslint-disable-next-line react-hooks/immutability -- 関数の宣言順序による既存パターンのため、リファクタリング時に修正する
        handleCheckLog(logUUID, false)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.logs])

  const isCheckedAll = () => {
    if (props.logs.length === 0) return false
    const successLogs = props.logs.filter(
      (log: ExcelConversionLog) =>
        log.status === ExcelConversionLogStatusEnum.SUCCESS,
    )
    if (successLogs.length === 0) return false
    const unChecked = successLogs.filter(
      (log: ExcelConversionLog) =>
        log.id !== undefined &&
        !Object.hasOwn(props.chooseLogUUIDs, log.logFile?.file?.uuid || ''),
    )
    return unChecked.length === 0
  }

  const handleCheckLog = (fileUUID: string | undefined, checked: boolean) => {
    if (fileUUID === undefined) {
      return
    }
    if (checked) {
      // eslint-disable-next-line react-hooks/immutability -- 関数の宣言順序による既存パターンのため、リファクタリング時に修正する
      props.chooseLogUUIDs[fileUUID] = fileUUID
      props.setChooseLogUUIDs({ ...props.chooseLogUUIDs })
    } else {
      delete props.chooseLogUUIDs[fileUUID]
      props.setChooseLogUUIDs({ ...props.chooseLogUUIDs })
    }
  }

  const handleCheckAll = (checked: boolean) => {
    if (checked) {
      props.logs
        .filter(log => log.status === ExcelConversionLogStatusEnum.SUCCESS)
        .forEach(log => {
          if (!log.logFile?.file?.uuid) {
            return
          }
          props.chooseLogUUIDs[log.logFile.file.uuid] = log.logFile.file.uuid
        })
      props.setChooseLogUUIDs({ ...props.chooseLogUUIDs })
    } else {
      props.logs.forEach(log => {
        if (!log.logFile?.file?.uuid) {
          return
        }
        delete props.chooseLogUUIDs[log.logFile.file.uuid]
      })
      props.setChooseLogUUIDs({ ...props.chooseLogUUIDs })
    }
  }

  return (
    <TableContainer>
      <Table sx={{ backgroundColor: white }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: white }}>
            <TableCell padding="checkbox">
              <Checkbox
                checked={isCheckedAll()}
                onChange={e => handleCheckAll(e.target.checked)}
              />
            </TableCell>
            <TableCell size="small" sx={{ width: '15%', fontWeight: 'bold' }}>
              日付
            </TableCell>
            <TableCell size="small" sx={{ width: '10%', fontWeight: 'bold' }}>
              実行結果
            </TableCell>
            <TableCell size="small" sx={{ width: '25%', fontWeight: 'bold' }}>
              変換フロー名
            </TableCell>
            <TableCell size="small" sx={{ width: '25%', fontWeight: 'bold' }}>
              作成ファイル名
            </TableCell>
            <TableCell size="small" sx={{ width: '15%', fontWeight: 'bold' }}>
              現場名
            </TableCell>
            <TableCell size="small" sx={{ width: '10%' }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {props.logs.map((log: ExcelConversionLog, index: number) => (
            <TableRow
              key={log.logFile?.file?.uuid ?? log.id ?? index}
              sx={{
                backgroundColor: white,
              }}
            >
              <TableCell padding="checkbox">
                <Checkbox
                  disabled={
                    log.status === ExcelConversionLogStatusEnum.FAILED &&
                    !log.logFile?.file?.uuid
                  }
                  checked={
                    log.id
                      ? Object.hasOwn(
                          props.chooseLogUUIDs,
                          log.logFile?.file?.uuid || '',
                        )
                      : false
                  }
                  onChange={e =>
                    log.id &&
                    handleCheckLog(log.logFile?.file?.uuid, e.target.checked)
                  }
                />
              </TableCell>
              <TableCell size="small">
                <Typography variant="body2">
                  {date.formatYYYYMMDD(log.createdAt)}
                </Typography>
              </TableCell>
              <TableCell size="small">{getStatusBadge(log.status)}</TableCell>
              <TableCell size="small">
                <Typography variant="body2">{log.flowName || ''}</Typography>
              </TableCell>
              <TableCell size="small">
                <Typography variant="body2">
                  {log.logFile?.file?.name || ''}
                </Typography>
              </TableCell>
              <TableCell size="small">
                <Stack>
                  {log.placeNodes?.map((placeNode: PlaceNode) => (
                    <Typography key={placeNode.uuid} variant="body2">
                      {placeNode.place?.name ?? ''}
                    </Typography>
                  ))}
                </Stack>
              </TableCell>
              <TableCell size="small" sx={{ p: 0 }}>
                {log.id !== undefined &&
                  log.status === ExcelConversionLogStatusEnum.SUCCESS && (
                    <Button
                      onClick={() => {
                        const fileUUID = log.logFile?.file?.uuid
                        if (fileUUID === undefined) return
                        downloadSingleFile({ fileUUID })
                      }}
                      variant="text"
                      size="small"
                    >
                      ダウンロード
                    </Button>
                  )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default ExcelConversionLogList
